/**
 * ProtectedRoute - Auth, Paywall & Admin Guard Component
 *
 * Wraps routes that require authentication.
 * Shows loading screen while checking auth state.
 * Redirects to /login if user is not authenticated.
 *
 * Paywall behavior:
 * - Paywalled users trying to access /create are redirected to /
 *   with ?paywall=1 so the dashboard shows the PaywallModal.
 * - All other routes are accessible (dashboard, editor).
 *
 * Password-change behavior:
 * - Users with a pending mandatory password change (temporary password set
 *   by support) are sent to /change-password from EVERY protected route,
 *   admin routes included. Only /change-password itself sets skipPasswordGuard.
 *
 * Admin behavior:
 * - When requireAdmin is true, checks admin role via API
 * - Non-admin users are redirected to /
 *
 * Usage:
 *   <Route path="/create" element={
 *     <ProtectedRoute><CreatePage /></ProtectedRoute>
 *   } />
 *   <Route path="/admin" element={
 *     <ProtectedRoute requireAdmin><AdminPage /></ProtectedRoute>
 *   } />
 */

import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserStatus } from '@/contexts/UserStatusContext';
import { useTranslation } from '@/contexts/LanguageContext';
import LoadingScreen from '@/components/ui/loading-screen';
import { checkAdminRole } from '@/services/adminService';

// Routes that are BLOCKED for paywalled users (redirected to dashboard + paywall modal)
const PAYWALL_BLOCKED_ROUTES = ['/create'];

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  /** Skip the onboarding guard for this route (used by /onboarding itself) */
  skipOnboardingGuard?: boolean;
  /** Skip the mandatory password-change guard (used by /change-password itself) */
  skipPasswordGuard?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin, skipOnboardingGuard, skipPasswordGuard }) => {
  const { t } = useTranslation();
  const { user, loading: authLoading, isConfigured, impersonation } = useAuth();
  const { status, isPaywalled, loading: statusLoading, passwordChangePending } = useUserStatus();
  const location = useLocation();

  const [adminChecked, setAdminChecked] = useState(!requireAdmin);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check admin role when required
  useEffect(() => {
    // Aba de suporte nunca acessa rotas admin (a API também recusa).
    if (!requireAdmin || !user || impersonation) {
      setAdminChecked(true);
      return;
    }

    checkAdminRole()
      .then((data) => setIsAdmin(data.isAdmin))
      .catch(() => setIsAdmin(false))
      .finally(() => setAdminChecked(true));
  }, [requireAdmin, user, impersonation]);

  // If Supabase is not configured, allow access (dev mode)
  if (!isConfigured) {
    return <>{children}</>;
  }

  // Show loading while checking auth, trial status, or admin role
  if (authLoading || statusLoading || !adminChecked) {
    return <LoadingScreen message={t('common.checkingAuth')} />;
  }

  // Redirect to login if not authenticated, preserving the intended destination
  if (!user) {
    const redirectTo = location.pathname !== '/'
      ? `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`
      : '/login';
    return <Navigate to={redirectTo} replace />;
  }

  // Mandatory password change: blocks everything else (before admin/onboarding)
  // Na aba de suporte não há troca de senha nem onboarding (o suporte não pode concluí-los).
  if (!skipPasswordGuard && passwordChangePending && !impersonation) {
    const intended = location.pathname + location.search;
    return <Navigate to={`/change-password?redirect=${encodeURIComponent(intended)}`} replace />;
  }

  // Admin routes: redirect non-admins to dashboard
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Onboarding guard: redirect to /onboarding if user hasn't completed it
  // Skip for routes that set skipOnboardingGuard (the onboarding page itself)
  if (!skipOnboardingGuard && status?.onboardingCompleted === false && !impersonation) {
    return <Navigate to="/onboarding" replace />;
  }

  // If paywalled and trying to access a blocked route, redirect to dashboard with paywall flag
  if (isPaywalled && PAYWALL_BLOCKED_ROUTES.includes(location.pathname)) {
    return <Navigate to="/?paywall=1" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
